"use client";

import React, { useState, useMemo } from "react";
import { ArrowRight, Sparkles, CircleCheck } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const categories = ["Tümü", "Gülüş Tasarımı", "İmplant", "Diş Beyazlatma", "Zirkonyum", "Ortodonti"];
const transformations = [{
  id: 1,
  category: "Gülüş Tasarımı",
  title: "Hollywood Smile Tasarımı",
  description: "Estetik ve fonksiyonel bir gülüş için 10 seanslık özel değişim.",
  beforeImg: "https://dtaysegul.10web.cloud/wp-content/uploads/2026/04/Yakinda.jpg?v=6xyrbm8nvzu",
  afterImg: "https://dtaysegul.10web.cloud/wp-content/uploads/2026/04/Yakinda.jpg?v=du18amo9ryf",
  patient: "Selin M."
}, {
  id: 2,
  category: "Ortodonti",
  title: "Şeffaf Plak Tedavisi",
  description: "12 aylık görünmez ortodonti süreci ile kusursuz hizalanma.",
  beforeImg: "https://dtaysegul.10web.cloud/wp-content/uploads/2026/04/Yakinda.jpg?v=73epqo939hv",
  afterImg: "https://dtaysegul.10web.cloud/wp-content/uploads/2026/04/Yakinda.jpg?v=fksbs6894t",
  patient: "Kaan K."
}, {
  id: 3,
  category: "İmplant",
  title: "Full Ark İmplant",
  description: "Eksik dişlerin modern implant teknolojisi ile restorasyonu.",
  beforeImg: "https://dtaysegul.10web.cloud/wp-content/uploads/2026/04/Yakinda.jpg?v=hclohefw4iq",
  afterImg: "https://dtaysegul.10web.cloud/wp-content/uploads/2026/04/Yakinda.jpg?v=7k2uv2w21hl",
  patient: "Levent R."
}, {
  id: 4,
  category: "Gülüş Tasarımı",
  title: "Porselen Lamina Veneer",
  description: "Doğal form ve renk geçişleri ile mükemmel estetik sonuç.",
  beforeImg: "https://dtaysegul.10web.cloud/wp-content/uploads/2026/04/Yakinda.jpg?v=ilp53oyazth",
  afterImg: "https://dtaysegul.10web.cloud/wp-content/uploads/2026/04/Yakinda.jpg?v=z5mrk845qcg",
  patient: "Elif E."
}, {
  id: 5,
  category: "Zirkonyum",
  title: "Zirkonyum Restorasyon",
  description: "Eski metal destekli köprülerin estetik zirkonyum ile yenilenmesi.",
  beforeImg: "https://dtaysegul.10web.cloud/wp-content/uploads/2026/04/Yakinda.jpg?v=8f4r5na99d5",
  afterImg: "https://dtaysegul.10web.cloud/wp-content/uploads/2026/04/Yakinda.jpg?v=4v5jokm4u7",
  patient: "Mert T."
}, {
  id: 6,
  category: "Diş Beyazlatma",
  title: "Klinik Tipi Beyazlatma",
  description: "Tek seansta yılların lekelerinden arınmış ışıltılı bir gülüş.",
  beforeImg: "https://dtaysegul.10web.cloud/wp-content/uploads/2026/04/Yakinda.jpg?v=pqbkgz4xlxp",
  afterImg: "https://dtaysegul.10web.cloud/wp-content/uploads/2026/04/Yakinda.jpg?v=8u2tx1zr8kf",
  patient: "Buse B."
}];
export default function SmileGallery() {
  const [activeCategory, setActiveCategory] = useState("Tümü");
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const filteredItems = useMemo(() => {
    if (activeCategory === "Tümü") return transformations;
    return transformations.filter(item => item.category === activeCategory);
  }, [activeCategory]);
  return <section data-section-id="41573" id="gülüş-tasarımı-sonuçları" ref={ref} className="bg-background relative overflow-hidden py-24 sm:py-32">
      {}
      <div className="bg-accent/20 absolute -top-24 -right-24 h-96 w-96 rounded-full blur-3xl" />
      <div className="bg-primary/5 absolute -bottom-24 -left-24 h-96 w-96 rounded-full blur-3xl" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className={`mx-auto max-w-3xl text-center transition-all duration-700 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="bg-secondary text-accent-foreground mb-4 inline-flex items-center gap-2 rounded-sm px-4 py-1.5 text-sm font-medium tracking-wider">
            <Sparkles className="h-4 w-4 text-primary" />
            <span>Gerçek Hasta Sonuçları</span>
          </div>
          <h2 className="text-foreground font-serif mb-6 text-3xl font-normal tracking-tight sm:text-4xl md:text-5xl">
            Değişim Galerisi
          </h2>
          <p className="text-muted-foreground font-sans text-lg leading-relaxed">
            Modern diş hekimliğinin dönüştürücü gücünü keşfedin. Uzman ellerde 
            şekillenen gerçek hasta hikayelerine ve estetik dokunuşlara tanıklık edin.
          </p>
        </div>

        {}
        <div className={`mt-12 flex flex-wrap justify-center gap-3 transition-all duration-700 delay-200 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          {categories.map((category, i) => <Button key={category} data-index={i} variant={activeCategory === category ? "default" : "outline"} onClick={() => setActiveCategory(category)} className={`rounded-sm px-6 transition-all duration-300 font-sans tracking-wide ${activeCategory === category ? "shadow-sm bg-primary text-primary-foreground border-primary" : "hover:bg-secondary hover:text-foreground bg-transparent border-border"}`}>
              {category}
            </Button>)}
        </div>

        {}
        <div className={`mt-16 transition-all duration-700 delay-300 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4">
              {filteredItems.map((item, i) => <CarouselItem key={`${item.id}-${activeCategory}`} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <div className="h-full p-1">
                    <Card className="bg-card border-border/40 h-full overflow-hidden rounded-sm border shadow-sm transition-all duration-500 hover:shadow-md">
                      <CardContent className="p-0">
                        {}
                        <div className="relative grid grid-cols-2 gap-0.5 bg-background">
                          <div className="group relative overflow-hidden">
                            <div className="absolute top-3 left-3 z-10 rounded-sm bg-foreground/80 px-2.5 py-1 text-[10px] font-medium text-background uppercase tracking-widest backdrop-blur-sm">
                              Önce
                            </div>
                            <img src={item.beforeImg} alt={`Before ${item.title}`} className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                          </div>
                          <div className="group relative overflow-hidden">
                            <div className="absolute top-3 right-3 z-10 rounded-sm bg-primary/90 px-2.5 py-1 text-[10px] font-medium text-primary-foreground uppercase tracking-widest backdrop-blur-sm">
                              Sonra
                            </div>
                            <img src={item.afterImg} alt={`After ${item.title}`} className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                          </div>
                        </div>

                        {}
                        <div className="p-6">
                          <div className="mb-2 flex items-center justify-between">
                            <span className="text-primary text-[10px] font-bold uppercase tracking-widest">
                              {item.category}
                            </span>
                            <span className="text-muted-foreground flex items-center gap-1 text-[11px]">
                              <CircleCheck className="h-3 w-3 text-primary" />
                              Onaylı Vaka
                            </span>
                          </div>
                          <h3 className="text-foreground font-serif mb-2 text-xl font-medium">
                            {item.title}
                          </h3>
                          <p className="text-muted-foreground font-sans mb-4 text-sm leading-relaxed">
                            {item.description}
                          </p>
                          <div className="border-t border-border/50 pt-4">
                            <p className="text-muted-foreground text-[11px] font-medium tracking-wide">
                              HASTA: <span className="text-foreground uppercase">{item.patient}</span>
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            <div className="mt-12 flex justify-center gap-4">
              <CarouselPrevious className="static translate-y-0 border-primary/20 hover:bg-primary hover:text-primary-foreground rounded-sm" />
              <CarouselNext className="static translate-y-0 border-primary/20 hover:bg-primary hover:text-primary-foreground rounded-sm" />
            </div>
          </Carousel>
        </div>

        {}
        <div className={`mt-16 text-center transition-all duration-700 delay-500 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <p className="text-muted-foreground font-sans mb-6 text-lg">
            Kendi değişim hikayenizi başlatmaya hazır mısınız?
          </p>
          <Button asChild size="lg" className="rounded-sm px-10 shadow-lg bg-primary hover:bg-primary/90 text-primary-foreground">
            <Link to="/ContactPage">
              Randevu Oluşturun
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}